import TemplateComposerControls from '@/components/templates/TemplateComposerControls';
import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import type { useEmailComposerTemplateFlow } from './use-email-composer-template-flow';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

interface EmailComposerTemplateControlsProps {
  templateFlow: ReturnType<typeof useEmailComposerTemplateFlow>;
  disabled?: boolean;
}

/**
 * Footer controls for the email composer: a "save as template" bookmark
 * (owner/admin only, once the body has content) and the brand template picker.
 * Mirrors the invitation modal's footer so both flows look identical. The
 * picker stays visible while empty to make language/context mismatches obvious.
 */
const EmailComposerTemplateControls = memo<EmailComposerTemplateControlsProps>(
  ({ templateFlow, disabled }) => {
    const { t } = useTranslation();
    const { canManage } = useIsBrandManager();

    const showSave = canManage && templateFlow.saveSeed.content.trim().length > 0;

    return (
      <TemplateComposerControls
        entries={templateFlow.entries}
        selectedEntryId={templateFlow.selectedEntryId}
        onSelect={templateFlow.selectEntry}
        disabled={disabled || templateFlow.attachmentsBusy}
        saveDisabled={templateFlow.attachmentsBlocked}
        confirmAttachmentReplacement={templateFlow.attachmentsCustomized}
        isLoading={templateFlow.isLoading}
        showSave={showSave}
        onSave={templateFlow.openSaveDrawer}
        saveLabel={t('templates.saveAsTemplate.action')}
      />
    );
  }
);

EmailComposerTemplateControls.displayName = 'EmailComposerTemplateControls';

export default EmailComposerTemplateControls;
